import { Fragment, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import {
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  UsersIcon,
  ChevronDownIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  Bars3Icon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  Squares2X2Icon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useOrganisations } from '@/features/organisations/hooks/useOrganisations';
import { useQuickAccess, useSettings } from '@/shared/hooks';
import type { Theme, LayoutMode } from '@/shared/hooks';
import { Avatar } from '@/shared/components/ui/Avatar';
import { GlobalSearch } from '@/shared/components/ui/GlobalSearch';
import { MobileDrawer } from '@/shared/components/ui/MobileDrawer';
import { XelacoreLogo } from '@/shared/components/ui/XelacoreLogo';
import { ToggleSwitch } from '@/shared/components/ui/ToggleSwitch';
import { cn } from '@/shared/utils/cn';

const typeColors: Record<string, string> = {
  licensee: 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300',
  licensor: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  retailer: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  agent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
};

const themeOptions: { value: Theme; label: string; icon: typeof SunIcon }[] = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'system', label: 'System', icon: ComputerDesktopIcon },
];

const layoutOptions: { value: LayoutMode; label: string; icon: typeof Squares2X2Icon }[] = [
  { value: 'container', label: 'Container', icon: Squares2X2Icon },
  { value: 'fluid', label: 'Fluid', icon: ArrowsPointingOutIcon },
];

export function AdminHeader() {
  const { name, logout } = useAuth();
  const location = useLocation();
  const { quickAccessOrgs, removeFromQuickAccess, updateQuickAccessOrg, clearQuickAccess, isInitialized: qaInitialized } = useQuickAccess();
  const { settings, setTheme, setLayoutMode, setViewProfile } = useSettings();

  const { data: orgsData } = useOrganisations({});
  useEffect(() => {
    if (!qaInitialized) return;
    const orgs = orgsData?.data?.organisations;
    if (!orgs || quickAccessOrgs.length === 0) return;
    const byId = new Map(orgs.map((o) => [o.organisation_id, o]));
    for (const qa of quickAccessOrgs) {
      const live = byId.get(qa.id);
      if (!live) {
        removeFromQuickAccess(qa.id);
        continue;
      }
      if (live.name !== qa.name || live.type !== qa.type) {
        updateQuickAccessOrg(qa.id, { name: live.name, type: live.type });
      }
    }
  }, [orgsData, qaInitialized, quickAccessOrgs, removeFromQuickAccess, updateQuickAccessOrg]);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // Extract display name (email or fallback)
  const displayName = name || 'Admin';
  const displayEmail = name && name.includes('@') ? name : null;

  return (
    <header className="sticky top-0 z-40 border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-800/80">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Left side: Logo + Navigation */}
          <div className="flex items-center space-x-4 md:space-x-8">
            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-lg p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] min-w-[44px]"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <div className="flex items-center gap-2">
                <XelacoreLogo width={120} height={24} className="text-gray-900 dark:text-white" />
                <span className="rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-gray-300 group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors">
                  Admin
                </span>
                {import.meta.env.VITE_STAGE && import.meta.env.VITE_STAGE !== 'live' && (
                  <span
                    className={cn(
                      'rounded-md px-2 py-0.5 text-xs font-semibold uppercase',
                      import.meta.env.VITE_STAGE === 'testing'
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                        : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                    )}
                  >
                    {import.meta.env.VITE_STAGE}
                  </span>
                )}
              </div>
            </Link>

            {/* Main Navigation */}
            <nav className="hidden md:flex md:items-center md:space-x-1">
              <Link
                to="/organisations"
                aria-current={isActive('/organisations') ? 'page' : undefined}
                className={cn(
                  'flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150',
                  isActive('/organisations')
                    ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                )}
              >
                <BuildingOffice2Icon className="h-5 w-5 mr-2" />
                Organisations
              </Link>
              <Link
                to="/users"
                aria-current={isActive('/users') ? 'page' : undefined}
                className={cn(
                  'flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150',
                  isActive('/users')
                    ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                )}
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                Users
              </Link>
            </nav>

            {/* Global Search */}
            <div className="hidden md:block">
              <GlobalSearch variant="input" className="w-64" />
            </div>
          </div>

          {/* Right side: Quick Access + User menu */}
          <div className="flex items-center space-x-1">
            {/* Quick Access Dropdown */}
            <Menu as="div" className="relative">
              <MenuButton
                className={cn(
                  'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
                  quickAccessOrgs.length > 0
                    ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-700 dark:hover:text-amber-300'
                    : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-500 dark:hover:text-gray-400'
                )}
                disabled={quickAccessOrgs.length === 0}
                onClick={() => setConfirmingClear(false)}
              >
                <StarIcon className="h-5 w-5" />
                {quickAccessOrgs.length > 0 && (
                  <>
                    <span className="hidden md:block md:ml-2">Quick Access</span>
                    <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                      {quickAccessOrgs.length}
                    </span>
                    <ChevronDownIcon className="hidden md:block ml-1 h-4 w-4 text-amber-400" />
                  </>
                )}
              </MenuButton>

              {quickAccessOrgs.length > 0 && (
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems className="absolute right-0 z-10 mt-2 w-[calc(100vw-2rem)] max-w-[500px] sm:w-[500px] origin-top-right rounded-lg bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Favourite Organisations
                      </p>
                      {confirmingClear ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              clearQuickAccess();
                              setConfirmingClear(false);
                            }}
                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                            aria-label="Confirm clear all"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setConfirmingClear(false);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            aria-label="Cancel"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setConfirmingClear(true);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                          aria-label="Clear all favourites"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto p-2">
                      <div className="grid grid-cols-2 gap-1">
                        {quickAccessOrgs.map((org) => (
                          <MenuItem key={org.id}>
                            {({ focus, close }) => (
                              <div
                                className={cn(
                                  'flex items-center justify-between px-3 py-2 rounded-md',
                                  focus && 'bg-gray-50 dark:bg-gray-700'
                                )}
                              >
                                <Link
                                  to={`/organisations/${org.id}/company-info`}
                                  onClick={() => close()}
                                  className="flex-1 min-w-0"
                                >
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">
                                    {org.name}
                                  </span>
                                  <span
                                    className={cn(
                                      'inline-block mt-0.5 px-1.5 py-0.5 text-xs font-medium rounded capitalize',
                                      typeColors[org.type] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    )}
                                  >
                                    {org.type}
                                  </span>
                                </Link>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeFromQuickAccess(org.id);
                                  }}
                                  className="ml-2 p-1 text-amber-400 hover:text-amber-600 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30 flex-shrink-0"
                                  aria-label="Remove from favourites"
                                >
                                  <StarIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </MenuItem>
                        ))}
                      </div>
                    </div>
                  </MenuItems>
                </Transition>
              )}
            </Menu>

            {/* User Dropdown */}
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 cursor-pointer">
                <Avatar name={displayName} size="sm" variant="primary" />
                <span className="hidden md:block md:ml-2 max-w-[180px] truncate">
                  {displayName}
                </span>
                <ChevronDownIcon className="hidden md:block ml-1 h-4 w-4 text-gray-400 dark:text-gray-500" />
              </MenuButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-lg bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <Avatar name={displayName} size="lg" variant="primary" />
                      <div className="min-w-0 flex-1">
                        {displayEmail ? (
                          <>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {displayEmail}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Administrator</p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {displayName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Administrator</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Theme Switcher */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Theme
                    </p>
                    <div className="flex gap-1">
                      {themeOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = settings.theme === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setTheme(option.value);
                            }}
                            className={cn(
                              'flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium transition-colors min-h-[36px]',
                              isSelected
                                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                            )}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="hidden sm:inline">{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Layout Switcher */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Layout
                    </p>
                    <div className="flex gap-1">
                      {layoutOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = settings.layoutMode === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setLayoutMode(option.value);
                            }}
                            className={cn(
                              'flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium transition-colors min-h-[36px]',
                              isSelected
                                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                            )}
                          >
                            <Icon className="h-4 w-4" />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* DAT mode switcher */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          DAT mode
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                          Condensed list-style sidebar tuned for the Data team.
                        </p>
                      </div>
                      <ToggleSwitch
                        checked={settings.viewProfile === 'dat'}
                        onChange={(checked) => setViewProfile(checked ? 'dat' : 'standard')}
                        size="sm"
                      />
                    </div>
                  </div>

                  {/* Sign Out */}
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={logout}
                        className={cn(
                          'flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer',
                          focus && 'bg-gray-50 dark:bg-gray-700'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
                        Sign out
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <MobileDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        position="left"
        title="Menu"
      >
        <div className="flex flex-col h-full">
          {/* Search */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <GlobalSearch variant="input" className="w-full" />
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 px-2 py-4 space-y-1">
            <Link
              to="/organisations"
              onClick={() => setMobileMenuOpen(false)}
              aria-current={isActive('/organisations') ? 'page' : undefined}
              className={cn(
                'flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-150 min-h-[44px]',
                isActive('/organisations')
                  ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
              )}
            >
              <BuildingOffice2Icon className="h-6 w-6 mr-3" />
              Organisations
            </Link>
            <Link
              to="/users"
              onClick={() => setMobileMenuOpen(false)}
              aria-current={isActive('/users') ? 'page' : undefined}
              className={cn(
                'flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-150 min-h-[44px]',
                isActive('/users')
                  ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
              )}
            >
              <UsersIcon className="h-6 w-6 mr-3" />
              Users
            </Link>
          </nav>

          {/* Quick Access Section */}
          {quickAccessOrgs.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                Favourite Organisations
              </p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {quickAccessOrgs.map((org) => (
                  <Link
                    key={org.id}
                    to={`/organisations/${org.id}/company-info`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">
                        {org.name}
                      </span>
                      <span
                        className={cn(
                          'inline-block mt-0.5 px-1.5 py-0.5 text-xs font-medium rounded capitalize',
                          typeColors[org.type] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        )}
                      >
                        {org.type}
                      </span>
                    </div>
                    <StarIcon className="h-4 w-4 text-amber-400 flex-shrink-0 ml-2" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </MobileDrawer>
    </header>
  );
}
